import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import type { Customer } from "../lib/types";
import {
  Button,
  Card,
  EmptyState,
  ErrorNote,
  Field,
  Input,
  PageHeader,
  Table,
  TableWrap,
  Td,
  Th,
  formatDate,
  formatRelative,
} from "../components/ui";

/** What issuing an invitation returns. The link comes back whether or not it
 *  was emailed, so an admin is never stuck when mail is not configured. */
interface InviteResult {
  url: string;
  email: string;
  emailed: boolean;
}

export function Customers() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState<Customer | "new" | null>(null);
  const [invited, setInvited] = useState<InviteResult & { name: string } | null>(null);
  const [confirming, setConfirming] = useState<string | null>(null);

  const customers = useQuery({
    queryKey: ["customers", search],
    queryFn: () =>
      api<Customer[]>(
        `/api/v1/admin/customers${search.trim().length > 0 ? `?search=${encodeURIComponent(search.trim())}` : ""}`,
      ),
  });

  const save = useMutation({
    mutationFn: (input: { id?: string; body: Record<string, unknown> }) =>
      input.id === undefined
        ? api("/api/v1/admin/customers", { method: "POST", body: input.body })
        : api(`/api/v1/admin/customers/${input.id}`, { method: "PATCH", body: input.body }),
    onSuccess: () => {
      setEditing(null);
      void queryClient.invalidateQueries({ queryKey: ["customers"] });
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => api(`/api/v1/admin/customers/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      setConfirming(null);
      void queryClient.invalidateQueries({ queryKey: ["customers"] });
    },
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Customers"
        lede="Who licences are issued to."
        action={
          <Button variant="primary" onClick={() => setEditing("new")}>
            Add customer
          </Button>
        }
      />

      <div className="max-w-sm">
        <Field label="Search">
          <Input
            value={search}
            placeholder="Name or email"
            onChange={(event) => setSearch(event.target.value)}
          />
        </Field>
      </div>

      {/* Shown once. Only its hash is stored, so losing it means issuing a new
          invitation rather than looking this one up again. */}
      {invited !== null && (
        <Card title={`Invitation for ${invited.name}`}>
          <div className="space-y-4 p-4">
            <p className="text-sm text-muted">
              {invited.emailed
                ? `Sent to ${invited.email}. Here is the same link, for the times somebody is on the phone or the mail does not arrive.`
                : "No mail provider is connected, so nothing was sent — pass this on yourself."}{" "}
              They choose their own password and set up two-factor authentication, and the link
              stops working once they have.
            </p>
            <code className="block break-all rounded border border-accent/40 bg-base px-3 py-3 tabular text-ink">
              {invited.url}
            </code>
            <div className="flex gap-2">
              <Button
                variant="primary"
                onClick={() => void navigator.clipboard.writeText(invited.url)}
              >
                Copy link
              </Button>
              <Button onClick={() => setInvited(null)}>Done</Button>
            </div>
          </div>
        </Card>
      )}

      {editing !== null && (
        <CustomerForm
          customer={editing === "new" ? null : editing}
          pending={save.isPending}
          error={save.isError ? (save.error as Error).message : null}
          onCancel={() => setEditing(null)}
          onSubmit={(body) =>
            save.mutate({ ...(editing === "new" ? {} : { id: editing.id }), body })
          }
        />
      )}

      <Card>
        {customers.isPending ? (
          <EmptyState>Loading…</EmptyState>
        ) : (customers.data?.length ?? 0) === 0 ? (
          <EmptyState>No customers match.</EmptyState>
        ) : (
          <TableWrap>
            <Table>
              <thead className="border-b border-hairline">
                <tr>
                  <Th>Name</Th>
                  <Th>Email</Th>
                  <Th>Phone</Th>
                  <Th>Licences</Th>
                  {/* More useful here than the Stripe id, which is still on
                      the edit form. Whether someone can sign in is a decision
                      an admin makes from this list. */}
                  <Th>Sign-in</Th>
                  <Th>Added</Th>
                  {/* Named for screen readers rather than left blank. A column
                      with no header is announced as nothing at all. */}
                  <Th className="sr-only">Actions</Th>
                </tr>
              </thead>
              <tbody>
                {(customers.data ?? []).map((customer) => (
                  <tr key={customer.id} className="border-b border-hairline last:border-0">
                    <Td label="Name">{customer.name}</Td>
                    <Td label="Email" className="text-muted">{customer.email}</Td>
                    <Td label="Phone" className="tabular text-muted">
                      {customer.phone ?? <span className="text-dim">—</span>}
                    </Td>
                    <Td label="Licences" className="tabular text-muted">
                      {customer._count?.licenses ?? 0}
                    </Td>
                    <Td label="Sign-in">
                      <SignInCell
                        customer={customer}
                        onInvited={(result) => setInvited({ ...result, name: customer.name })}
                      />
                    </Td>
                    <Td label="Added" className="text-muted">{formatDate(customer.createdAt)}</Td>
                    {/* The column has no header to borrow, so the card says
                        what the control does. */}
                    <Td label="Actions">
                      {confirming === customer.id ? (
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-xs text-muted">Delete {customer.name}?</span>
                          <Button
                            variant="danger"
                            disabled={remove.isPending}
                            onClick={() => remove.mutate(customer.id)}
                          >
                            Delete
                          </Button>
                          <Button onClick={() => setConfirming(null)}>Keep</Button>
                        </div>
                      ) : (
                        <div className="flex gap-2">
                          <Button variant="ghost" onClick={() => setEditing(customer)}>
                            Edit
                          </Button>
                          <Button variant="ghost" onClick={() => setConfirming(customer.id)}>
                            Delete
                          </Button>
                        </div>
                      )}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableWrap>
        )}
        {remove.isError && (
          <div className="px-4 pb-4">
            <ErrorNote>{(remove.error as Error).message}</ErrorNote>
          </div>
        )}
      </Card>
    </div>
  );
}

/**
 * Most customers never get a login — a marketer closes the sale, we host it,
 * and nobody needs an account for the licence to work. So this offers one
 * rather than nagging for it, and says plainly when an invitation is already
 * out rather than letting an admin send a second.
 */
function SignInCell({
  customer,
  onInvited,
}: {
  customer: Customer;
  onInvited: (result: InviteResult) => void;
}) {
  const queryClient = useQueryClient();
  const account = customer.adminUsers?.[0];
  const outstanding = customer.invites?.[0];

  const invite = useMutation({
    mutationFn: () =>
      api<InviteResult>(`/api/v1/admin/customers/${customer.id}/invite`, {
        method: "POST",
        body: {},
      }),
    onSuccess: (result) => {
      onInvited(result);
      void queryClient.invalidateQueries({ queryKey: ["customers"] });
    },
  });

  if (account !== undefined) {
    return (
      <span className="text-sm text-muted">
        {account.lastLoginAt === null ? "Never signed in" : formatRelative(account.lastLoginAt)}
      </span>
    );
  }

  return (
    <div className="flex flex-col items-start gap-1">
      {outstanding !== undefined && (
        <span className="text-xs text-pending">
          Invited, expires {formatDate(outstanding.expiresAt)}
        </span>
      )}
      <Button variant="ghost" disabled={invite.isPending} onClick={() => invite.mutate()}>
        {invite.isPending ? "Creating…" : outstanding === undefined ? "Invite" : "New link"}
      </Button>
      {invite.isError && (
        <span className="text-xs text-danger">{(invite.error as Error).message}</span>
      )}
    </div>
  );
}

function CustomerForm({
  customer,
  pending,
  error,
  onCancel,
  onSubmit,
}: {
  customer: Customer | null;
  pending: boolean;
  error: string | null;
  onCancel: () => void;
  onSubmit: (body: Record<string, unknown>) => void;
}) {
  const [name, setName] = useState(customer?.name ?? "");
  const [email, setEmail] = useState(customer?.email ?? "");
  const [phone, setPhone] = useState(customer?.phone ?? "");
  const [company, setCompany] = useState(customer?.company ?? "");
  const [stripeCustomerId, setStripe] = useState(customer?.stripeCustomerId ?? "");
  const [notes, setNotes] = useState(customer?.notes ?? "");

  return (
    <Card title={customer === null ? "Add customer" : `Edit ${customer.name}`}>
      <form
        className="space-y-4 p-4"
        onSubmit={(event) => {
          event.preventDefault();
          onSubmit({ name, email, phone, company, stripeCustomerId, notes });
        }}
      >
        <div className="grid gap-4 md:grid-cols-2">
          <Field label="Name">
            <Input value={name} required onChange={(event) => setName(event.target.value)} />
          </Field>
          <Field label="Email">
            <Input
              type="email"
              value={email}
              required
              onChange={(event) => setEmail(event.target.value)}
            />
          </Field>
          {/* A marketer can close a sale with a name, an email and a phone
              number, and the customer may never create a login. */}
          <Field label="Phone">
            <Input value={phone} onChange={(event) => setPhone(event.target.value)} />
          </Field>
          <Field label="Company">
            <Input value={company} onChange={(event) => setCompany(event.target.value)} />
          </Field>
          <Field label="Stripe customer" hint="Filled in automatically once billing is connected.">
            <Input
              value={stripeCustomerId}
              className="tabular"
              onChange={(event) => setStripe(event.target.value)}
            />
          </Field>
          <Field label="Notes">
            <Input value={notes} onChange={(event) => setNotes(event.target.value)} />
          </Field>
        </div>

        {error !== null && <ErrorNote>{error}</ErrorNote>}

        <div className="flex gap-2">
          <Button type="submit" variant="primary" disabled={pending}>
            {pending ? "Saving…" : "Save customer"}
          </Button>
          <Button type="button" variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </form>
    </Card>
  );
}
